import { icon } from '@carvana/ds-styles';
import { Info } from 'lucide-react';
import { Fragment, useEffect, useMemo } from 'react';
import { absoluteBaseUrl } from '~/constants/environment';
import {
  useDealershipConfiguration,
  useDealershipDepartmentHours,
  useDealershipId
} from '~/context/dealership/StaticContext';
import buildDealershipUrl from '~/utilities/dealership/buildDealershipUrl';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import {
  Divider,
  Footer,
  Heading,
  Hours,
  HoursHeading,
  LinksOuterWrapper,
  LinksWrapper
} from './PageFooter.styles';

type LegalLink =
  | { href: string; sameTab?: boolean; text: string }
  | { onClick: () => void; text: string };

const OT_PREFERENCES_HASH = '#ot-preferences';
const OT_PREFERENCES_RETRY_INTERVAL_MS = 500;
const OT_PREFERENCES_TIMEOUT_MS = 15000;

function getLegalLinks(dealershipId: string | null): Array<LegalLink> {
  return [
    {
      href: buildDealershipUrl(`${absoluteBaseUrl}/terms-of-use`, dealershipId),
      text: 'User Agreement'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/financial-and-other-privacy-notices`,
        dealershipId
      ),
      text: 'Financial and Other Privacy Notices'
    },
    {
      href: '/privacy-policy',
      sameTab: true,
      text: 'Consumer Privacy Notice'
    },
    {
      onClick: () => window.OneTrust?.ToggleInfoDisplay?.(),
      text: 'Do Not Sell or Share My Personal Information'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/code-of-conduct/`,
        dealershipId
      ),
      text: 'Code of Conduct'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/responsible-disclosure`,
        dealershipId
      ),
      text: 'Responsible Disclosure'
    },
    {
      href: buildDealershipUrl(
        `${absoluteBaseUrl}/accessibility`,
        dealershipId
      ),
      text: 'Accessibility'
    }
  ];
}

export default function PageFooter() {
  const dealershipId = useDealershipId();
  const { name } = useDealershipConfiguration();
  const departmentHours = useDealershipDepartmentHours();
  const legalLinks = useMemo(() => getLegalLinks(dealershipId), [dealershipId]);

  useEffect(() => {
    if (window.location.hash !== OT_PREFERENCES_HASH) {
      return undefined;
    }

    const isPreferenceCenterOpen = () => {
      const preferenceCenter = document.getElementById('onetrust-pc-sdk');

      return (
        !!preferenceCenter &&
        preferenceCenter.getBoundingClientRect().height > 0
      );
    };

    let intervalId: ReturnType<typeof setInterval> | undefined;

    // ToggleInfoDisplay silently no-ops while the SDK is still initializing, so keep retrying
    // until the overlay is up. It toggles, so never call it once the modal is already open.
    const openPreferenceCenter = () => {
      if (isPreferenceCenterOpen()) {
        clearInterval(intervalId);

        return;
      }

      window.OneTrust?.ToggleInfoDisplay?.();
    };

    openPreferenceCenter();

    intervalId = setInterval(
      openPreferenceCenter,
      OT_PREFERENCES_RETRY_INTERVAL_MS
    );

    const timeoutId = setTimeout(
      () => clearInterval(intervalId),
      OT_PREFERENCES_TIMEOUT_MS
    );

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Footer>
      <div className="flex flex-col gap-y-4xl md:grid md:grid-cols-3 lg:grid-cols-[1fr_2fr_1fr]">
        <LinksOuterWrapper>
          <LinksWrapper>
            <Heading>Financing</Heading>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/financing?openSoftpullModal=true`,
                dealershipId,
                'softpull'
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="get-pre-qualified"
              data-analytics-id={null}
              data-testid="get-pre-qualified"
            >
              Get Pre-Qualified
            </a>
          </LinksWrapper>

          <LinksWrapper>
            <Heading>Trade/Sell</Heading>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/sell-my-car`,
                dealershipId,
                'stc'
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="sell-my-car"
              data-analytics-id={null}
              data-testid="get-an-offer"
            >
              Get an Offer
            </a>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/value-tracker`,
                dealershipId
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="value-tracker"
              data-analytics-id={null}
              data-testid="value-tracker"
            >
              Carvana Value Tracker
            </a>
          </LinksWrapper>
        </LinksOuterWrapper>

        <LinksOuterWrapper className="lg:grid lg:grid-cols-2">
          <LinksWrapper>
            <Heading>About {name}</Heading>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/about-us`,
                dealershipId
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="about-us"
              data-analytics-id={null}
              data-testid="about-us"
            >
              About Us
            </a>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/reviews`,
                dealershipId
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="customer-reviews"
              data-analytics-id={null}
              data-testid="customer-reviews"
            >
              Customer Reviews
            </a>
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/careers`,
                dealershipId
              )}
              target="_blank"
              rel="noopener"
              data-adobe-id="careers"
              data-analytics-id={null}
              data-testid="careers"
            >
              Careers
            </a>
          </LinksWrapper>

          <LinksWrapper>
            <Heading>Support</Heading>
            <a
              href={buildDealershipUrl(`${absoluteBaseUrl}/help`, dealershipId)}
              target="_blank"
              rel="noopener"
              className="flex items-center gap-x-xl"
              data-adobe-id="support-and-contact"
              data-analytics-id={null}
              data-testid="support-and-contact"
            >
              <Info size={32} color={icon.primarySubtle} />
              Support &amp; Contact
            </a>
          </LinksWrapper>
        </LinksOuterWrapper>

        <LinksWrapper>
          <Heading>Hours</Heading>

          {departmentHours?.map((department, index) => (
            <Fragment key={department.label}>
              {index > 0 && <Divider />}

              <HoursHeading>{department.label}</HoursHeading>
              <Hours>
                {department.daysHours.map(({ days, hours }) => (
                  <Fragment key={`${department.label}-${days}`}>
                    <div>{days}</div>
                    <div className="text-right">{hours}</div>
                  </Fragment>
                ))}
              </Hours>
            </Fragment>
          ))}
        </LinksWrapper>
      </div>

      <Divider />

      <div className="flex flex-col gap-y-xl">
        <p>
          Copyright © {new Date().getFullYear()} Carvana, LLC. All Rights
          Reserved.
        </p>
        <p className="flex flex-wrap gap-x-sm">
          {legalLinks.map((link, index) => (
            <Fragment key={link.text}>
              {index > 0 && ' | '}
              {'onClick' in link ? (
                <button
                  type="button"
                  onClick={link.onClick}
                  className="inline h-auto w-auto cursor-pointer bg-transparent p-0 text-inherit"
                  data-adobe-id={toLowerKebabCase(link.text)}
                  data-analytics-id={null}
                  data-testid="legal-link"
                >
                  {link.text}
                </button>
              ) : (
                <a
                  href={link.href}
                  target={link.sameTab ? undefined : '_blank'}
                  rel={link.sameTab ? undefined : 'noopener'}
                  data-adobe-id={toLowerKebabCase(link.text)}
                  data-analytics-id={null}
                  data-testid="legal-link"
                >
                  {link.text}
                </a>
              )}
            </Fragment>
          ))}
        </p>
      </div>
    </Footer>
  );
}
